// src/contexts/ToastContext.js
import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import './ToastStyles.css';

// Tipos de toast
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Contexto para el sistema de toast
const ToastContext = createContext();

// Hook personalizado para usar el contexto
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe ser usado dentro de un ToastProvider');
  }
  return context;
};

// Componente individual de toast
const Toast = ({ id, type, message, onClose }) => {
  // Código sin cambios, mantener como está
  const getIcon = () => {
    switch (type) {
      case TOAST_TYPES.SUCCESS:
        return '✅';
      case TOAST_TYPES.ERROR:
        return '❌';
      case TOAST_TYPES.WARNING:
        return '⚠️';
      case TOAST_TYPES.INFO:
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className={`toast-item ${type}`}>
      <div className="toast-icon">{getIcon()}</div>
      <div className="toast-content">{message}</div>
      <button className="toast-close" onClick={() => onClose(id)}>×</button>
    </div>
  );
};

// Proveedor del sistema de toast - MODIFICAR ESTA PARTE
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastIdCounter = useRef(0);
  // Esta ref guardará los toasts pendientes
  const pendingToastsRef = useRef([]);
  // Esta ref indicará si estamos procesando toasts
  const isProcessingRef = useRef(false);

  // Procesa los toasts pendientes de manera segura
  const processToastsQueue = useCallback(() => {
    if (pendingToastsRef.current.length > 0 && !isProcessingRef.current) {
      isProcessingRef.current = true;

      const pendingToasts = [...pendingToastsRef.current];
      pendingToastsRef.current = [];

      setToasts(prevToasts => [...prevToasts, ...pendingToasts]);
      setTimeout(() => {
        isProcessingRef.current = false;
        if (pendingToastsRef.current.length > 0) {
          processToastsQueue();
        }
      }, 0);
    }
  }, []);

  // Añadir un nuevo toast
  const addToast = useCallback((type, message, duration = 5000) => {
    // Crear ID único con contador incremental
    const id = `toast-${Date.now()}-${toastIdCounter.current++}`;

    // Crear nuevo toast
    const newToast = {
      id,
      type,
      message,
    };

    // Agregar a la cola en lugar de actualizar estado directamente
    pendingToastsRef.current.push(newToast);

    // Procesar la cola
    processToastsQueue();

    // Auto-eliminar después del tiempo especificado
    if (duration) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [processToastsQueue]);

  // Remover un toast específico
  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  // Funciones de conveniencia para diferentes tipos de toast
  const success = useCallback((message, duration) =>
    addToast(TOAST_TYPES.SUCCESS, message, duration), [addToast]);

  const error = useCallback((message, duration) =>
    addToast(TOAST_TYPES.ERROR, message, duration), [addToast]);

  const warning = useCallback((message, duration) =>
    addToast(TOAST_TYPES.WARNING, message, duration), [addToast]);

  const info = useCallback((message, duration) =>
    addToast(TOAST_TYPES.INFO, message, duration), [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;